import { Check, X } from 'lucide-react';
import { DynamicIcon } from 'lucide-react/dynamic';
import { useState } from 'react';
import { Alert, Button, Card, Col, Row, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { Card as CardType, TaskApprovalRequest } from '../../service/api';

interface ManagerTaskApprovalCardProps {
  approval: TaskApprovalRequest;
  onApprove: (taskId: string, recurrenceId: string) => Promise<void>;
  onReject: (taskId: string, recurrenceId: string) => Promise<void>;
}

export default function ManagerTaskApprovalCard({
  approval,
  onApprove,
  onReject,
}: ManagerTaskApprovalCardProps) {
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleApprove = async () => {
    setIsProcessing(true);
    setError(null);
    try {
      await onApprove(approval._id, approval.recurrence._id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!window.confirm(t('manager.approvals.confirmReject'))) return;

    setIsProcessing(true);
    setError(null);
    try {
      await onReject(approval._id, approval.recurrence._id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Card className="mb-3 shadow-sm border">
      <Card.Body>
        <Row className="align-items-center mb-3">
          <Col xs="auto">
            <DynamicIcon
              name={approval.recurrence.icon}
              size={32}
              className="text-primary"
            />
          </Col>
          <Col>
            <h6 className="mb-1">{approval.recurrence.title}</h6>
            <p className="text-muted small mb-1">
              {t('manager.approvals.completedBy')}:{' '}
              {approval.assignedTo.given_name} {approval.assignedTo.family_name}
            </p>
            <p className="text-muted small mb-0">
              {t('manager.approvals.submittedAt')}:{' '}
              {approval.updatedAt.toFormat('MMM d, h:mm a')}
            </p>
          </Col>
          <Col xs="auto" className="d-flex align-items-center gap-2">
            {typeof approval.recurrence.rewardCard !== 'string' &&
              (approval.recurrence.rewardCard as CardType).actions
                ?.filter(a => a.type === 'sticker')
                .map((action, idx) => (
                  <span key={idx}>
                    <span className="fw-bold">{action.amount}</span>{' '}
                    <span className="text-muted">{t('common.stickers')}</span>
                  </span>
                ))}
          </Col>
        </Row>

        <div className="d-flex gap-2 justify-content-end">
          <Button
            variant="success"
            size="sm"
            onClick={handleApprove}
            disabled={isProcessing}
            className="d-flex align-items-center gap-1"
          >
            {isProcessing ? (
              <Spinner size="sm" />
            ) : (
              <>
                <Check size={16} />
                {t('manager.approvals.approve')}
              </>
            )}
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={handleReject}
            disabled={isProcessing}
            className="d-flex align-items-center gap-1"
          >
            {isProcessing ? (
              <Spinner size="sm" />
            ) : (
              <>
                <X size={16} />
                {t('manager.approvals.reject')}
              </>
            )}
          </Button>
        </div>

        {error && (
          <Alert variant="danger" className="mt-3 mb-0 py-2">
            {error}
          </Alert>
        )}
      </Card.Body>
    </Card>
  );
}
